import { classifyLevel, type AudioLevel } from '../hooks/useAudioLevel';

/**
 * The "is it hearing me?" strip under the record button: a level bar per
 * source, a speaking / silence pill, and a one-line hint when something is
 * wrong (too quiet, clipping, nothing at all). Reads only; the hook decides.
 */

interface MeetlyLevelMeterProps {
  label: string;
  reading: AudioLevel;
  /** Shown when the source is expected but not producing anything. */
  compact?: boolean;
}

const BAND_CLASS: Record<ReturnType<typeof classifyLevel>, string> = {
  silent: 'bg-[var(--gt-border-strong)]',
  quiet: 'bg-[#e6a23c]',
  good: 'bg-[var(--gt-primary)]',
  loud: 'bg-[#e6a23c]',
  clipping: 'bg-[#e66a52]',
};

export function levelHint(reading: AudioLevel): string | null {
  if (!reading.active) return null;
  const band = classifyLevel(reading.db, reading.peak);
  if (band === 'clipping') return 'Too loud — the input is clipping. Move the mic away or lower its gain.';
  if (reading.silentForSeconds >= 20 && band === 'silent') return 'Nothing is coming in. Check the microphone is unmuted and the right device is selected.';
  if (reading.silentForSeconds >= 20 && band === 'quiet') return 'Very quiet. Move closer to the microphone or bring the laptop nearer the people talking.';
  return null;
}

export default function MeetlyLevelMeter({ label, reading, compact = false }: MeetlyLevelMeterProps) {
  const band = classifyLevel(reading.db, reading.peak);
  const width = Math.round(reading.level * 100);
  const peakLeft = Math.round(reading.peak * 100);
  const status = !reading.active
    ? 'Not capturing'
    : reading.speaking
      ? 'Hearing speech'
      : reading.silentForSeconds >= 3
        ? `Silence for ${reading.silentForSeconds}s`
        : 'Listening';

  return (
    <div className={`flex items-center gap-3 ${compact ? 'text-[11px]' : 'text-xs'}`} data-testid={`level-${label.toLowerCase()}`}>
      <span className="w-14 shrink-0 text-left font-medium text-[var(--gt-text-secondary)]">{label}</span>
      <div
        className="relative h-2 flex-1 overflow-hidden rounded-full bg-[var(--gt-surface-2)]"
        role="meter"
        aria-label={`${label} input level`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={width}
        aria-valuetext={reading.active ? `${Math.round(reading.db)} dB` : 'not capturing'}
      >
        <div className={`h-full rounded-full transition-[width] duration-75 ${BAND_CLASS[band]}`} style={{ width: `${width}%` }} />
        {reading.active && (
          <span className="absolute top-0 h-full w-0.5 bg-[var(--gt-text-secondary)]/60" style={{ left: `calc(${peakLeft}% - 1px)` }} aria-hidden="true" />
        )}
      </div>
      <span
        className={`w-28 shrink-0 text-right font-medium ${reading.speaking ? 'text-[var(--gt-primary)]' : 'text-[var(--gt-text-tertiary)]'}`}
        aria-live="polite"
      >
        {status}
      </span>
    </div>
  );
}
